import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import type { PLANNER_ANALYTICS_SOURCE } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';
import {
  GuestlistAnalyticsActionTypes,
  ClickedToShareGuestCollectorLinkAnalyticsPayload,
  InteractedWithGuestsOnGuestlistAnalyticsPayload,
} from '@bridebook/web/lib/guestlist/analytics/action-types';
import {
  IGuestlistCategory,
  AddedMultipleGuestsMethod,
  GuestlistMethod,
} from '@bridebook/web/lib/guestlist/types';

export const addedGuestAnalytics = (payload: {
  edit: boolean;
  guest: IGuest;
  method: 'inline' | 'plus1' | typeof PLANNER_ANALYTICS_SOURCE;
}) => ({
  type: GuestlistAnalyticsActionTypes.ADDED_GUEST,
  payload,
});

export const addedMultipleGuestsAnalytics = (payload: {
  category: IGuestlistCategory;
  count: number;
  method: AddedMultipleGuestsMethod;
}) => ({
  type: GuestlistAnalyticsActionTypes.ADDED_MULTIPLE_GUESTS,
  payload,
});

// A guest's details changed. The method doubles as the action, as on the guest list.
export const editedGuestAnalytics = (payload: {
  guest: IGuest;
  method: GuestlistMethod;
  editedGuestAction: GuestlistMethod;
}) => ({
  type: GuestlistAnalyticsActionTypes.EDITED_GUEST,
  payload,
});

export const deletedMultipleGuestsAnalytics = (payload: {
  count: number;
  method: GuestlistMethod;
}) => ({
  type: GuestlistAnalyticsActionTypes.DELETED_MULTIPLE_GUESTS,
  payload,
});

export const clickedDeleteGuestAnalytics = () => ({
  type: GuestlistAnalyticsActionTypes.CLICKED_DELETE_GUEST,
});

export const deletedGuestAnalytics = (payload: { method: GuestlistMethod }) => ({
  type: GuestlistAnalyticsActionTypes.DELETED_GUEST,
  payload,
});

export const clickedResetGuestlistAnalytics = () => ({
  type: GuestlistAnalyticsActionTypes.CLICKED_RESET_GUESTLIST,
});

export const resetGuestlistAnalytics = () => ({
  type: GuestlistAnalyticsActionTypes.RESET_GUESTLIST,
});

export const cancelResetGuestlistAnalytics = () => ({
  type: GuestlistAnalyticsActionTypes.CANCELLED_RESET_GUESTLIST,
});

export const initialisedGuestlistAnalytics = (
  method: 'bulkAddGuestPopup' | 'importContacts' | 'emptyGuestList',
) => ({
  type: GuestlistAnalyticsActionTypes.INITIALISED_GUESTLIST,
  payload: { method },
});

export const toggleBulkAddGuestsMethodAnalytics = (
  method: 'bulkAddGuestPopup' | 'importContacts',
) => ({
  type: GuestlistAnalyticsActionTypes.TOGGLE_BULK_ADD_METHOD,
  payload: { method },
});

export type ClickedAddGuestsMethod =
  | 'addGuestsManually'
  | 'importFromSpreadsheet'
  | 'importContacts';

export type ClickedAddGuestsSection = 'addGuestsDrawer' | 'emptyGuestList' | 'desktopDropDown';

export type ClickedAddGuestsCta =
  | 'Add'
  | 'Import'
  | 'Upload file'
  | 'Upload file again'
  | 'Confirm import'
  | 'Template';

export const clickedAddGuestsAnalytics = (payload: {
  method: ClickedAddGuestsMethod;
  clickedSection?: ClickedAddGuestsSection;
  cta?: ClickedAddGuestsCta;
}) => ({
  type: GuestlistAnalyticsActionTypes.CLICKED_ADD_GUESTS,
  payload,
});

export const toggleGuestCollectorPopupAnalytics = (payload: { show: boolean }) => ({
  type: payload.show
    ? GuestlistAnalyticsActionTypes.OPENED_GUEST_COLLECTOR_POPUP
    : GuestlistAnalyticsActionTypes.CLOSED_GUEST_COLLECTOR_POPUP,
  payload,
});

export const clickedToShareGuestCollectorLinkAnalytics = (
  payload: ClickedToShareGuestCollectorLinkAnalyticsPayload,
) => ({
  type: GuestlistAnalyticsActionTypes.CLICKED_TO_SHARE_GUEST_COLLECTOR_LINK,
  payload,
});

export const sharedGuestCollectorLinkAnalytics = (payload: {
  url: string;
  appChosen?: string;
  collectGuestInfoPopupLocation?: string;
  section?: string;
}) => ({
  type: GuestlistAnalyticsActionTypes.SHARED_GUEST_COLLECTOR_LINK,
  payload,
});

export const interactedWithGuestsOnGuestlistAnalytics = (
  payload: InteractedWithGuestsOnGuestlistAnalyticsPayload,
) => ({
  type: GuestlistAnalyticsActionTypes.INTERACTED_WITH_GUESTS_ON_GUESTLIST,
  payload,
});
